import React, { useState, useEffect } from 'react';
import {
    CurrencyDollarIcon,
    GlobeAltIcon,
    ArrowPathIcon,
    ExclamationTriangleIcon,
    CheckCircleIcon,
    InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { useCurrency } from '../../contexts/CurrencyContext';
import CurrencySelector from './CurrencySelector';
import CurrencyDisplay from './CurrencyDisplay';
import LoadingSpinner from '../ui/LoadingSpinner';
import { cn } from '../../utils/cn';

interface CurrencySettingsProps {
    className?: string;
    showTitle?: boolean;
}

const CurrencySettings: React.FC<CurrencySettingsProps> = ({
    className = '',
    showTitle = true,
}) => {
    const {
        userCurrency,
        availableCurrencies,
        setUserCurrency,
        refreshRates,
        isLoadingRates,
        isLoadingSupportedCurrencies,
        error,
        clearError,
        getCurrencyInfo,
        detectUserCurrency,
    } = useCurrency();

    const [lastRefreshTime, setLastRefreshTime] = useState<Date | null>(null);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [isDetectingCurrency, setIsDetectingCurrency] = useState(false);

    const selectedCurrency = getCurrencyInfo(userCurrency);

    // Update refresh time when rates are refreshed
    useEffect(() => {
        if (!isLoadingRates) {
            setLastRefreshTime(new Date());
        }
    }, [isLoadingRates]);

    // Show success message when currency changes
    useEffect(() => {
        setShowSuccessMessage(true);
        const timer = setTimeout(() => setShowSuccessMessage(false), 3000);
        return () => clearTimeout(timer);
    }, [userCurrency]);

    const handleCurrencyChange = (currency: string) => {
        setUserCurrency(currency);
        clearError();
    };

    const handleRefreshRates = async () => {
        try {
            await refreshRates();
            setLastRefreshTime(new Date());
        } catch (error) {
            console.error('Failed to refresh rates:', error);
        }
    };

    const handleAutoDetect = async () => {
        try {
            setIsDetectingCurrency(true);
            await detectUserCurrency();
        } catch (error) {
            console.error('Failed to detect currency:', error);
        } finally {
            setIsDetectingCurrency(false);
        }
    };

    const formatLastRefreshTime = () => {
        if (!lastRefreshTime) return 'Never';

        const now = new Date();
        const diff = now.getTime() - lastRefreshTime.getTime();
        const minutes = Math.floor(diff / (1000 * 60));

        if (minutes < 1) return 'Just now';
        if (minutes === 1) return '1 minute ago';
        if (minutes < 60) return `${minutes} minutes ago`;

        const hours = Math.floor(minutes / 60);
        if (hours === 1) return '1 hour ago';
        if (hours < 24) return `${hours} hours ago`;

        return lastRefreshTime.toLocaleDateString();
    };

    const renderError = () => {
        if (!error) return null;

        return (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-md">
                <div className="flex items-start">
                    <ExclamationTriangleIcon className="w-5 h-5 text-red-400 dark:text-red-300 mt-0.5 mr-2 flex-shrink-0" />
                    <div className="flex-1">
                        <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                        <button
                            onClick={clearError}
                            className="mt-1 text-sm text-red-600 dark:text-red-300 hover:text-red-800 dark:hover:text-red-100 underline"
                        >
                            Dismiss
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderSuccessMessage = () => {
        if (!showSuccessMessage) return null;

        return (
            <div className="mb-4 p-3 bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-700 rounded-md">
                <div className="flex items-start">
                    <CheckCircleIcon className="w-5 h-5 text-green-400 dark:text-green-300 mt-0.5 mr-2 flex-shrink-0" />
                    <div className="flex-1">
                        <p className="text-sm text-green-800 dark:text-green-200">
                            Currency updated to {selectedCurrency?.name} ({userCurrency})
                        </p>
                    </div>
                </div>
            </div>
        );
    };

    const renderCurrentCurrencyInfo = () => {
        if (!selectedCurrency) return null;

        return (
            <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md p-4 mb-6">
                <div className="flex items-start">
                    <InformationCircleIcon className="w-5 h-5 text-gray-400 dark:text-gray-300 mt-0.5 mr-3 flex-shrink-0" />
                    <div className="flex-1">
                        <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-3 sm:mb-2">
                            Current Currency
                        </h4>
                        <div className="space-y-3 sm:space-y-2 text-sm text-gray-700 dark:text-gray-300">
                            {/* Currency Info - Stack on mobile, side-by-side on desktop */}
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                                <span className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1 sm:mb-0">Currency:</span>
                                <span className="font-semibold text-gray-800 dark:text-gray-200 text-sm sm:text-sm">
                                    {selectedCurrency.name} ({selectedCurrency.code})
                                </span>
                            </div>

                            {/* Symbol Info */}
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                                <span className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1 sm:mb-0">Symbol:</span>
                                <span className="font-semibold text-gray-800 dark:text-gray-200 text-lg sm:text-sm">
                                    {selectedCurrency.symbol}
                                </span>
                            </div>

                            {/* Sample Amount Info */}
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                                <span className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1 sm:mb-0">Sample Amount:</span>
                                <CurrencyDisplay
                                    amount={100}
                                    currency={userCurrency}
                                    className="font-bold text-gray-900 dark:text-gray-100 text-base sm:text-sm"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderRateInfo = () => {
        return (
            <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md p-4">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div className="flex items-start">
                        <GlobeAltIcon className="w-5 h-5 text-gray-400 dark:text-gray-500 mt-0.5 mr-3 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                            <h4 className="text-sm font-medium text-gray-800 dark:text-gray-100 mb-2">
                                Exchange Rate Information
                            </h4>
                            <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                                <div>Last updated: {formatLastRefreshTime()}</div>
                                <div>
                                    Rates are automatically refreshed every 15 minutes
                                </div>
                                <div className="hidden sm:block">
                                    Source: European Central Bank via Frankfurter API
                                </div>
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={handleRefreshRates}
                        disabled={isLoadingRates}
                        className={cn(
                            'flex items-center justify-center gap-2 px-3 py-1.5 text-sm rounded-md transition-colors min-w-max',
                            isLoadingRates
                                ? 'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                                : 'bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500'
                        )}
                        title="Refresh exchange rates"
                    >
                        <ArrowPathIcon
                            className={cn(
                                'w-4 h-4',
                                isLoadingRates && 'animate-spin'
                            )}
                        />
                        <span className="hidden xs:inline">{isLoadingRates ? 'Refreshing...' : 'Refresh'}</span>
                        <span className="xs:hidden">{isLoadingRates ? '...' : 'Refresh'}</span>
                    </button>
                </div>
            </div>
        );
    };

    if (isLoadingSupportedCurrencies) {
        return (
            <div className={cn('space-y-4 sm:space-y-6', className)}>
                {showTitle && (
                    <div>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                            Currency Settings
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            Choose your preferred currency for displaying prices and payments.
                        </p>
                    </div>
                )}

                <div className="flex items-center justify-center py-8">
                    <LoadingSpinner size="lg" />
                </div>
            </div>
        );
    }

    return (
        <div className={cn('space-y-4 sm:space-y-6', className)}>
            {showTitle && (
                <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        Currency Settings
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                        Choose your preferred currency for displaying prices and payments.
                        All amounts will be converted automatically using current exchange rates.
                    </p>
                </div>
            )}

            {renderError()}
            {renderSuccessMessage()}
            {renderCurrentCurrencyInfo()}

            {/* Currency Selector */}
            <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Preferred Currency
                </label>

                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex-1 min-w-0">
                        <CurrencySelector
                            value={userCurrency}
                            onChange={handleCurrencyChange}
                            showFlags={true}
                            showCurrencyNames={true}
                            searchable={true}
                            popular={true}
                            regional={true}
                            groupByRegion={true}
                            showRecentlyUsed={true}
                            placeholder="Select your preferred currency"
                        />
                    </div>

                    <button
                        onClick={handleAutoDetect}
                        disabled={isDetectingCurrency}
                        className={cn(
                            'flex items-center justify-center gap-2 px-3 py-2 text-sm border rounded-md transition-colors min-w-max',
                            isDetectingCurrency
                                ? 'bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed border-gray-300 dark:border-gray-500'
                                : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 border-gray-300 dark:border-gray-500'
                        )}
                        title="Auto-detect currency based on your location"
                    >
                        {isDetectingCurrency ? (
                            <LoadingSpinner size="xs" />
                        ) : (
                            <GlobeAltIcon className="w-4 h-4" />
                        )}
                        <span className="hidden xs:inline sm:hidden md:inline">Auto-detect</span>
                        <span className="xs:hidden sm:inline md:hidden">Auto</span>
                    </button>
                </div>

                <p className="text-xs text-gray-500 dark:text-gray-400">
                    Choose the currency you'd like to see prices displayed in.
                    Original amounts will still be shown for reference.
                </p>
            </div>

            {/* Sample Conversions */}
            <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Sample Conversions
                </label>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    {[
                        { amount: 50, currency: 'USD', label: '$50 USD' },
                        { amount: 100, currency: 'GHS', label: '₵100 GHS' },
                        { amount: 75, currency: 'EUR', label: '€75 EUR' },
                    ].map(({ amount, currency, label }) => (
                        <div key={currency} className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md p-2 sm:p-3">
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">{label}</div>
                            <CurrencyDisplay
                                amount={amount}
                                currency={currency}
                                showOriginal={currency !== userCurrency}
                                showTooltip={true}
                                size="sm"
                                className="font-medium"
                            />
                        </div>
                    ))}
                </div>
            </div>

            {renderRateInfo()}
        </div>
    );
};

export default CurrencySettings;